import React, { useState, useRef } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ChatInput = ({ onSendMessage, onAttachImage, isTyping, disabled }) => {
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const fileInputRef = useRef(null);

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSendMessage(message.trim());
      setMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        onAttachImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const quickSuggestions = [
    "I need a gift for my mom's birthday",
    "Looking for anniversary gift ideas",
    "What\'s good for a housewarming?",
    "Need something under $50"
  ];

  return (
    <div className="bg-white border-t border-accent-200 p-4">
      {/* Quick Suggestions */}
      {message === '' && (
        <div className="mb-4">
          <p className="text-sm text-text-secondary mb-2">Quick suggestions:</p>
          <div className="flex flex-wrap gap-2">
            {quickSuggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => setMessage(suggestion)}
                className="px-3 py-1 bg-surface text-accent-600 text-sm rounded-full hover:bg-primary-50 hover:text-primary transition-colors"
                disabled={disabled}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Typing Indicator */}
      {isTyping && (
        <div className="flex items-center space-x-2 mb-3 text-text-secondary">
          <div className="flex space-x-1">
            <div className="w-2 h-2 bg-secondary rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
          <span className="text-sm">AI is thinking...</span>
        </div>
      )}

      {/* Input Area */}
      <div className="flex items-end space-x-3">
        {/* Attachment Button */}
        <div className="flex-shrink-0">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
          <Button
            variant="ghost"
            size="sm"
            iconName="Paperclip"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            className="text-accent-500 hover:text-accent-700"
          />
        </div>

        {/* Message Input */}
        <div className="flex-1 relative">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Describe what you're looking for or ask a question..."
            disabled={disabled}
            className="w-full px-4 py-3 pr-12 border border-accent-200 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary text-sm"
            rows={1}
            style={{ minHeight: '48px', maxHeight: '120px' }}
          />
          
          {/* Character Count */}
          {message.length > 0 && (
            <div className="absolute bottom-2 right-12 text-xs text-text-muted">
              {message.length}/500
            </div>
          )}
        </div>

        {/* Voice Message Button */}
        <div className="flex-shrink-0">
          <Button
            variant={isRecording ? "danger" : "ghost"}
            size="sm"
            iconName={isRecording ? "Square" : "Mic"}
            onClick={() => setIsRecording(!isRecording)}
            disabled={disabled}
            className={isRecording ? "animate-pulse" : "text-accent-500 hover:text-accent-700"}
          />
        </div>

        {/* Send Button */}
        <div className="flex-shrink-0">
          <Button
            variant="primary"
            size="sm"
            iconName="Send"
            onClick={handleSend}
            disabled={!message.trim() || disabled}
            className="shadow-warm hover:shadow-primary"
          />
        </div>
      </div>

      {/* Recording Indicator */}
      {isRecording && (
        <div className="mt-3 flex items-center justify-center space-x-2 text-error">
          <div className="w-3 h-3 bg-error rounded-full animate-pulse"></div>
          <span className="text-sm font-medium">Recording... Tap to stop</span>
        </div>
      )}
    </div>
  );
};

export default ChatInput;